import { useMemo } from 'react';
import { Select } from 'antd';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { CanonicalMeasurement, MeasurementLabel } from '@/models/template';
import rowStyles from '@/features/report/Measurements/Row/MeasurementRow.module.css';
import { EditableText } from '../../primitives/EditableText';
import { RowControls } from '../../primitives/RowControls';
import styles from './EditableMeasurementRow.module.css';

interface EditableMeasurementRowProps {
  measurementLabel: MeasurementLabel;
  position: string;
  selectedCanonicalMeasurement: CanonicalMeasurement | null;
  canonicalMeasurements: CanonicalMeasurement[];
  usedKeys: Set<string>;
  onMeasurementLabelChange: (measurementLabel: MeasurementLabel) => void;
  onDelete: () => void;
}

export function EditableMeasurementRow({
  measurementLabel,
  position,
  selectedCanonicalMeasurement,
  canonicalMeasurements,
  usedKeys,
  onMeasurementLabelChange,
  onDelete,
}: EditableMeasurementRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: measurementLabel.uuid });

  // `transform` and `transition` are recomputed per drag frame by dnd-kit, so
  // they must stay inline. `isDragging` toggles a CSS class for the faint
  // placeholder treatment while the DragOverlay shows the floating ghost.
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Filter out canonicals used by other rows; always keep this row's own
  // selection so it remains visible in its own picker.
  const canonicalMeasurementOptions = useMemo(
    () =>
      canonicalMeasurements
        .filter(
          (c) =>
            !usedKeys.has(c.key) || c.key === selectedCanonicalMeasurement?.key
        )
        .map((c) => ({
          value: c.key,
          abbr: c.abbreviation,
          label: `${c.abbreviation} — ${c.name}`,
        })),
    [canonicalMeasurements, usedKeys, selectedCanonicalMeasurement]
  );

  const handleLabelChange = (value: string) => {
    onMeasurementLabelChange({ ...measurementLabel, label: value });
  };

  const handleCanonicalMeasurementChange = (key: string | undefined) => {
    const picked = key
      ? (canonicalMeasurements.find((c) => c.key === key) ?? null)
      : null;
    onMeasurementLabelChange({
      ...measurementLabel,
      canonicalMeasurement: picked?.uuid ?? null,
      canonicalMeasurementKey: picked?.key ?? null,
      unit: picked?.unit ?? null,
      precision: picked?.precision ?? null,
    });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={styles.row}
      data-editable-row
      data-dragging={isDragging || undefined}
      {...attributes}
      {...listeners}
    >
      <span className={styles.positionBadge}>{position}.</span>
      <span className={`${rowStyles.label} ${styles.labelCell}`}>
        <EditableText
          value={measurementLabel.label}
          onChange={handleLabelChange}
          placeholder="Label"
        />
      </span>
      <span className={styles.pickerCell}>
        <Select
          variant="borderless"
          size="small"
          placeholder="Pick"
          value={selectedCanonicalMeasurement?.key ?? undefined}
          onChange={handleCanonicalMeasurementChange}
          options={canonicalMeasurementOptions}
          showSearch={{
            filterOption: (input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase()),
          }}
          allowClear
          optionLabelProp="abbr"
          popupMatchSelectWidth={320}
          className={styles.canonicalSelect}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
      </span>
      <span className={rowStyles.unit}>{measurementLabel.unit ?? ''}</span>
      <RowControls onDelete={onDelete} className={styles.deleteCell} />
    </div>
  );
}
